import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, Cpu, FlaskConical, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import { JsonField, tryParseJson } from '@/components/agents/JsonField';
import {
  useGetAgentModelsQuery,
  useGetAgentProvidersQuery,
  useCreateAgentModelMutation,
  useUpdateAgentModelMutation,
  useDeleteAgentModelMutation,
  useTestAgentModelMutation,
} from '@/store/api/agentFactoryApi';
import type {
  AgentModelConfig,
  AgentProviderConfig,
  ModelTestResult,
  ModelToolChoice,
  ReasoningEffort,
} from '@/types';

interface ModelFormState {
  name: string;
  description: string;
  providerConfigId: string;
  modelName: string;
  // Sampling
  temperature: string;
  topP: string;
  topK: string;
  seed: string;
  frequencyPenalty: string;
  presencePenalty: string;
  // Tokens
  maxOutputTokens: string;
  stopSequences: string;
  // Reasoning
  reasoning: ReasoningEffort;
  // Tools
  toolChoice: ModelToolChoice;
  maxSteps: string;
  maxRetries: string;
  // Advanced
  timeoutTotalMs: string;
  timeoutStepMs: string;
  timeoutToolMs: string;
  extraProviderOptions: string;
}

const emptyForm: ModelFormState = {
  name: '',
  description: '',
  providerConfigId: '',
  modelName: '',
  temperature: '',
  topP: '',
  topK: '',
  seed: '',
  frequencyPenalty: '',
  presencePenalty: '',
  maxOutputTokens: '',
  stopSequences: '',
  reasoning: 'provider-default',
  toolChoice: 'auto',
  maxSteps: '',
  maxRetries: '',
  timeoutTotalMs: '',
  timeoutStepMs: '',
  timeoutToolMs: '',
  extraProviderOptions: '',
};

const REASONING_OPTIONS: ReasoningEffort[] = [
  'provider-default',
  'none',
  'minimal',
  'low',
  'medium',
  'high',
  'xhigh',
];

function fromModel(m: AgentModelConfig): ModelFormState {
  const s = m.settings ?? {};
  return {
    name: m.name,
    description: m.description ?? '',
    providerConfigId: m.providerConfigId,
    modelName: m.modelName,
    temperature: s.temperature != null ? String(s.temperature) : '',
    topP: s.topP != null ? String(s.topP) : '',
    topK: s.topK != null ? String(s.topK) : '',
    seed: s.seed != null ? String(s.seed) : '',
    frequencyPenalty: s.frequencyPenalty != null ? String(s.frequencyPenalty) : '',
    presencePenalty: s.presencePenalty != null ? String(s.presencePenalty) : '',
    maxOutputTokens: s.maxOutputTokens != null ? String(s.maxOutputTokens) : '',
    stopSequences: s.stopSequences?.join(', ') ?? '',
    reasoning: (s.reasoning as ReasoningEffort) ?? 'provider-default',
    toolChoice: (s.toolChoice as ModelToolChoice) ?? 'auto',
    maxSteps: s.maxSteps != null ? String(s.maxSteps) : '',
    maxRetries: s.maxRetries != null ? String(s.maxRetries) : '',
    timeoutTotalMs: s.timeout?.totalMs != null ? String(s.timeout.totalMs) : '',
    timeoutStepMs: s.timeout?.stepMs != null ? String(s.timeout.stepMs) : '',
    timeoutToolMs: s.timeout?.toolMs != null ? String(s.timeout.toolMs) : '',
    extraProviderOptions:
      m.extraProviderOptions && Object.keys(m.extraProviderOptions).length > 0
        ? JSON.stringify(m.extraProviderOptions, null, 2)
        : '',
  };
}

function toNum(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function ModelsPage() {
  const { success: toastSuccess, error: toastError } = useToast();

  const { data: models, isLoading, isError } = useGetAgentModelsQuery();
  const { data: providers } = useGetAgentProvidersQuery();
  const providerById = useMemo(() => {
    const map = new Map<string, AgentProviderConfig>();
    for (const p of providers ?? []) map.set(p._id, p);
    return map;
  }, [providers]);

  const [createModel, { isLoading: creating }] = useCreateAgentModelMutation();
  const [updateModel, { isLoading: updating }] = useUpdateAgentModelMutation();
  const [deleteModel, { isLoading: deleting }] = useDeleteAgentModelMutation();
  const [testModel, { isLoading: testing }] = useTestAgentModelMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ModelFormState>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<AgentModelConfig | null>(null);

  const [testTarget, setTestTarget] = useState<AgentModelConfig | null>(null);
  const [testResult, setTestResult] = useState<ModelTestResult | null>(null);

  const isSubmitting = creating || updating;

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (m: AgentModelConfig) => {
    setEditingId(m._id);
    setForm(fromModel(m));
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.modelName.trim() || !form.providerConfigId) return;
    const extra = tryParseJson(form.extraProviderOptions);
    const dto = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      providerConfigId: form.providerConfigId,
      modelName: form.modelName.trim(),
      modelType: 'language' as const,
      settings: {
        temperature: toNum(form.temperature),
        topP: toNum(form.topP),
        topK: toNum(form.topK),
        seed: toNum(form.seed),
        frequencyPenalty: toNum(form.frequencyPenalty),
        presencePenalty: toNum(form.presencePenalty),
        maxOutputTokens: toNum(form.maxOutputTokens),
        stopSequences:
          form.stopSequences.trim() === ''
            ? undefined
            : form.stopSequences.split(',').map((s) => s.trim()).filter(Boolean),
        reasoning: form.reasoning,
        toolChoice: form.toolChoice,
        maxSteps: toNum(form.maxSteps),
        maxRetries: toNum(form.maxRetries),
        timeout:
          form.timeoutTotalMs || form.timeoutStepMs || form.timeoutToolMs
            ? {
                totalMs: toNum(form.timeoutTotalMs),
                stepMs: toNum(form.timeoutStepMs),
                toolMs: toNum(form.timeoutToolMs),
              }
            : undefined,
      },
      extraProviderOptions:
        extra !== undefined ? (extra as Record<string, unknown>) : undefined,
    };
    try {
      if (editingId) {
        await updateModel({ id: editingId, body: dto }).unwrap();
        toastSuccess('Model updated');
      } else {
        await createModel(dto).unwrap();
        toastSuccess('Model created');
      }
      setDialogOpen(false);
    } catch (err) {
      toastError((err as any)?.data?.message || 'Failed to save model');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteModel(deleteTarget._id).unwrap();
      toastSuccess('Model deleted');
      setDeleteTarget(null);
    } catch {
      toastError('Failed to delete model');
    }
  };

  const handleTest = async (m: AgentModelConfig) => {
    setTestTarget(m);
    setTestResult(null);
    try {
      const result = await testModel(m._id).unwrap();
      setTestResult(result);
      if (result.ok) toastSuccess(`Model responded (${result.latencyMs}ms)`);
    } catch (err) {
      setTestResult({ ok: false, error: (err as any)?.data?.message || 'Test failed', latencyMs: 0 });
      toastError('Model test failed');
    }
  };

  const columns: ColumnDef<AgentModelConfig, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="truncate font-medium">{row.original.name}</p>
              {row.original.description && (
                <p className="truncate text-xs text-muted-foreground">{row.original.description}</p>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'providerConfigId',
        header: 'Provider',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {providerById.get(row.original.providerConfigId)?.name ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'modelName',
        header: 'Model',
        cell: ({ row }) => <span className="truncate font-mono text-xs">{row.original.modelName}</span>,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              title="Test model"
              disabled={testing}
              onClick={(e) => {
                e.stopPropagation();
                handleTest(row.original);
              }}
            >
              <FlaskConical className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                openEdit(row.original);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row.original);
              }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [providerById, testing],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Models</h1>
          <p className="text-sm text-muted-foreground">
            Per-model generation config (sampling, tokens, reasoning, tools).
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> New Model
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-0">
          <p className="text-sm font-medium">Configured models</p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : isError ? (
            <p className="py-8 text-center text-sm text-destructive">Failed to load models.</p>
          ) : !models || models.length === 0 ? (
            <EmptyState
              title="No models"
              description="Add a model on top of a provider to use it in collections."
              action={<Button onClick={openCreate}>New Model</Button>}
            />
          ) : (
            <DataTable columns={columns} data={models} onRowClick={() => {}} />
          )}
        </CardContent>
      </Card>

      {/* ── Create / edit form ───────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen} >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit model' : 'New model'}</DialogTitle>
            <DialogDescription>
              Settings are grouped per Appendix B. Advanced JSON passes through to the provider.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Basics */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="model-name">Name *</Label>
                <Input
                  id="model-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. GPT-4o"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model-name2">Model id *</Label>
                <Input
                  id="model-name2"
                  value={form.modelName}
                  onChange={(e) => setForm({ ...form, modelName: e.target.value })}
                  placeholder="gpt-4o"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Provider *</Label>
                <Select
                  value={form.providerConfigId}
                  onValueChange={(v) => setForm({ ...form, providerConfigId: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {(providers ?? []).map((p) => (
                      <SelectItem key={p._id} value={p._id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="model-desc">Description</Label>
                <Input
                  id="model-desc"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>

            {/* Sampling */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Sampling</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-3 pt-0">
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Temperature</span>
                  <Input
                    type="number" step="0.01"
                    value={form.temperature}
                    onChange={(e) => setForm({ ...form, temperature: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Top P</span>
                  <Input
                    type="number" step="0.01"
                    value={form.topP}
                    onChange={(e) => setForm({ ...form, topP: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Top K</span>
                  <Input
                    type="number"
                    value={form.topK}
                    onChange={(e) => setForm({ ...form, topK: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Seed</span>
                  <Input
                    type="number"
                    value={form.seed}
                    onChange={(e) => setForm({ ...form, seed: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Frequency penalty</span>
                  <Input
                    type="number" step="0.01"
                    value={form.frequencyPenalty}
                    onChange={(e) => setForm({ ...form, frequencyPenalty: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Presence penalty</span>
                  <Input
                    type="number" step="0.01"
                    value={form.presencePenalty}
                    onChange={(e) => setForm({ ...form, presencePenalty: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Tokens */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Tokens</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-3 pt-0">
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Max output tokens</span>
                  <Input
                    type="number"
                    value={form.maxOutputTokens}
                    onChange={(e) => setForm({ ...form, maxOutputTokens: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Stop sequences (comma-separated)</span>
                  <Input
                    value={form.stopSequences}
                    onChange={(e) => setForm({ ...form, stopSequences: e.target.value })}
                    placeholder="END, ASSISTANT"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Reasoning */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Reasoning</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Reasoning effort</span>
                  <Select
                    value={form.reasoning}
                    onValueChange={(v) => setForm({ ...form, reasoning: v as ReasoningEffort })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REASONING_OPTIONS.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Tools */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Tools</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-3 pt-0 sm:grid-cols-3">
                <div className="space-y-1 sm:col-span-3">
                  <span className="text-xs text-muted-foreground">Tool choice</span>
                  <Select
                    value={typeof form.toolChoice === 'string' ? form.toolChoice : 'required'}
                    onValueChange={(v) => setForm({ ...form, toolChoice: v as ModelToolChoice })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">auto</SelectItem>
                      <SelectItem value="none">none</SelectItem>
                      <SelectItem value="required">required</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Max steps</span>
                  <Input
                    type="number"
                    value={form.maxSteps}
                    onChange={(e) => setForm({ ...form, maxSteps: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Max retries</span>
                  <Input
                    type="number"
                    value={form.maxRetries}
                    onChange={(e) => setForm({ ...form, maxRetries: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Advanced */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Advanced</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">Timeout total (ms)</span>
                    <Input
                      type="number"
                      value={form.timeoutTotalMs}
                      onChange={(e) => setForm({ ...form, timeoutTotalMs: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">Timeout step (ms)</span>
                    <Input
                      type="number"
                      value={form.timeoutStepMs}
                      onChange={(e) => setForm({ ...form, timeoutStepMs: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">Timeout tool (ms)</span>
                    <Input
                      type="number"
                      value={form.timeoutToolMs}
                      onChange={(e) => setForm({ ...form, timeoutToolMs: e.target.value })}
                    />
                  </div>
                </div>
                <JsonField
                  label="Extra provider options (JSON)"
                  value={form.extraProviderOptions}
                  onChange={(v) => setForm({ ...form, extraProviderOptions: v })}
                  height={140}
                  hint="Merged into providerOptions.<provider> last (AF-6 passthrough)."
                />
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !form.name.trim() || !form.modelName.trim() || !form.providerConfigId}
            >
              {isSubmitting ? 'Saving…' : editingId ? 'Save changes' : 'Create model'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Test model dialog ────────────────────────────────────────────── */}
      <Dialog open={!!testTarget} onOpenChange={(open) => !open && setTestTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test model — {testTarget?.name}</DialogTitle>
            <DialogDescription>
              Live generation probe ({testTarget?.modelName}).
            </DialogDescription>
          </DialogHeader>
          {testing ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Testing model…
            </div>
          ) : testResult ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {testResult.ok ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-destructive" />
                )}
                <span className="font-medium">{testResult.ok ? 'Responded' : 'Test failed'}</span>
                <Badge variant="outline" className="ml-auto tabular-nums">
                  {testResult.latencyMs ?? '—'} ms
                </Badge>
              </div>
              {testResult.finishReason && (
                <p className="text-xs text-muted-foreground">
                  Finish reason: <span className="font-mono">{testResult.finishReason}</span>
                </p>
              )}
              {testResult.error && (
                <p className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
                  {testResult.error}
                </p>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button onClick={() => setTestTarget(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete model"
        description={`Are you sure you want to delete "${deleteTarget?.name ?? 'this model'}"?`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleting}
      />
    </div>
  );
}